import React, { useState, useEffect, Fragment, useRef } from "react";
import { Button } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2/Grid2";
import { FormControl } from "@mui/material";
import { TextField } from "@mui/material";
import { Select } from "@mui/material";
import { MenuItem } from "@mui/material";
import { useNavigate } from "react-router-dom";
import DialogBoxComponent from "../../../components/DialogBoxComponent";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import RichTextBoxTabPanel from "../../../components/RichTextBoxEditerTabs";
import dayjs from "dayjs";
import {
  createIncidentResponsePlaybook,
  updateIncidentResponsePlaybook,
} from "../helper";
import { useIncidentResponsePlaybookContext } from "../../../context/IncidentResponsePlaybook/IncidentResponsePlaybookContext";
import { convertToUTC, isClientAdmin } from "../../../utils/ConvertDate";
import { useUserContext } from "../../../context/auth/UserContext";
import AutoVersionIncrement from "../../../components/AutoVersionIncrement";
import { useLocation } from "react-router-dom";
import { useAccessRights } from "../../../utils/useAccessRights";

const initialFormState = {
  playbookName: "",
  preparation: "",
  detection: "",
  analysis: "",
  containment: "",
  eradication: "",
  recovery: "",
  reporting: "",
  version: "",
  originalVersion: "",
  versionIncrement: "",
  dateApproved: null,
  status: "",
};

export const IncidentResponsePlaybookForm = () => {
  const navigate = useNavigate();
  const incidentResponsePlayBookAccess = useAccessRights(
    "Incident Response PlayBook"
  );

  const location = useLocation();
  const { userId, companyProfileID, userAccess } = useUserContext();
  const [formValues, setFormValues] = useState(initialFormState);
  const [isUpdated, setIsUpdated] = useState(false);
  const isUpdatedRef = useRef(isUpdated);
  const [playbookFormTitle, setPlaybookFormTitle] = useState<string>(
    "Create Incident Response Playbook"
  );
  const [isUserStillOnPage, setIsUserStillOnPage] = useState(false);
  const [dialogState, setDialogState] = useState({
    title: "",
    body: "",
    action: "",
    actionCancel: "",
    openEditConfirmationDialog: false,
    openSuccessDialogBox: false,
  });

  const { playbookDetails } = useIncidentResponsePlaybookContext();

  const tabDetalis = [
    {
      label: "preparation",
      key: "preparation",
      id: `tab-preparation`,
      editerValue: formValues.preparation,
    },
    {
      label: "detection",
      key: "detection",
      id: `tab-detection`,
      editerValue: formValues.detection,
    },
    {
      label: "analysis",
      key: "analysis",
      id: `tab-analysis`,
      editerValue: formValues.analysis,
    },
    {
      label: "containment",
      key: "containment",
      id: `tab-containment`,
      editerValue: formValues.containment,
    },
    {
      label: "eradication",
      key: "eradication",
      id: `tab-eradication`,
      editerValue: formValues.eradication,
    },
    {
      label: "recovery",
      key: "recovery",
      id: `tab-recovery`,
      editerValue: formValues.recovery,
    },
    {
      label: "reporting",
      key: "reporting",
      id: `tab-reporting`,
      editerValue: formValues.reporting,
    },
  ];

  const resetPage = () => {
    navigate("/incident-response-playbook");
    setFormValues(initialFormState);
  };

  const handleCancel = async () => {
    resetPage();
  };

  const handleCloseDialog = () => {
    if (dialogState.title === "Success") {
      resetPage();
    }
    setDialogState({
      ...dialogState,
      openSuccessDialogBox: false,
      openEditConfirmationDialog: false,
    });
  };

  const handleCloseEditDialog = () => {
    setFormValues((prevVal) => ({ ...prevVal, status: "Approved" }));
    setDialogState({
      ...dialogState,
      openSuccessDialogBox: false,
      openEditConfirmationDialog: false,
    });
  };

  const handleTabEditerChange = (val: any, key: any) => {
    handleFormChange({
      target: { value: val, id: key },
    });
  };

  const handleFormChange = (e: { target: { id: any; value: any } }) => {
    const { id, value } = e.target;
    // console.log("handleFormChange", "ID", id, "VAL", value);

    setFormValues((prevValues) => ({
      ...prevValues,
      [id]: value,
    }));
  };

  const disableVersionChange = () => {
    if (
      playbookDetails?.playbookID &&
      playbookDetails.status !== formValues.status &&
      formValues.status === "Approved"
    ) {
      return false;
    }
    return true;
  };

  const disableForm = () => {
    if (
      formValues.playbookName === "" ||
      formValues.version === "" ||
      formValues.status === "" ||
      formValues.preparation === "" ||
      formValues.detection === "" ||
      formValues.analysis === "" ||
      formValues.containment === "" ||
      formValues.eradication === "" ||
      formValues.recovery === "" ||
      formValues.reporting === ""
    ) {
      return true;
    }

    if (formValues.status === "Approved") {
      if (
        !formValues.dateApproved ||
        (!disableVersionChange() && formValues.versionIncrement === "")
      ) {
        return true;
      }
    }
  };

  const handleCreatePlaybook = async () => {
    const date = new Date();
    const approvalDate = formValues?.dateApproved && formValues.dateApproved;
    const payload = [
      {
        playbookName: formValues.playbookName,
        preparation: formValues.preparation,
        detection: formValues.detection,
        analysis: formValues.analysis,
        containment: formValues.containment,
        eradication: formValues.eradication,
        recovery: formValues.recovery,
        reporting: formValues.reporting,
        version: formValues.version,
        status: formValues.status,
        dateApproved: convertToUTC(approvalDate),
        createDate: date,
        createdBy: userId,
        approvedBy: formValues.status === "Approved" ? userId : 0,
        companyProfileID: companyProfileID,
        isEditedByUserId: "0",
      },
    ];
    const res = await createIncidentResponsePlaybook(payload);
    if (res.succeeded === true) {
      setDialogState({
        title: "Success",
        body: `Playbook is successfully Created`,
        action: "Ok",
        actionCancel: "",
        openSuccessDialogBox: true,
        openEditConfirmationDialog: false,
      });
    } else {
      setDialogState({
        title: "Error",
        body: "Unexpected Error",
        action: "OK",
        actionCancel: "",
        openSuccessDialogBox: true,
        openEditConfirmationDialog: false,
      });
    }
  };

  const handleEditPlaybook = async () => {
    setIsUpdated(true);
    const date = new Date();

    const payload = [
      {
        playbookName: formValues.playbookName,
        preparation: formValues.preparation,
        detection: formValues.detection,
        analysis: formValues.analysis,
        containment: formValues.containment,
        eradication: formValues.eradication,
        recovery: formValues.recovery,
        reporting: formValues.reporting,
        version: formValues.version,
        status: formValues.status,
        dateApproved: convertToUTC(formValues.dateApproved),
        updatedDate: date,
        updatedBy: userId,
        approvedBy: formValues.status === "Approved" ? userId : 0,
        playbookID: playbookDetails.playbookID,
        companyProfileID: companyProfileID,
        isEditedByUser: false,
        isEditedByUserId: "0",
      },
    ];
    const res = await updateIncidentResponsePlaybook(payload);
    if (res.succeeded === true) {
      setDialogState({
        title: "Success",
        body: "Plan successfully updated",
        action: "OK",
        actionCancel: "",
        openSuccessDialogBox: true,
        openEditConfirmationDialog: false,
      });
    } else {
      setDialogState({
        title: "Error",
        body: "Unexpected Error",
        action: "OK",
        actionCancel: "",
        openSuccessDialogBox: true,
        openEditConfirmationDialog: false,
      });
    }
  };

  const handleEdit = () => {
    if (
      playbookDetails.status === "Approved" &&
      playbookDetails.status === formValues.status
    ) {
      setDialogState({
        title: "Confirmation",
        body: "Editing an Approved record will change the status to Ready to Approve . Do you want to continue?",
        action: "OK",
        actionCancel: "Cancel",
        openSuccessDialogBox: false,
        openEditConfirmationDialog: true,
      });
      setFormValues((prevVal) => ({ ...prevVal, status: "Ready to Approve" }));
    } else {
      handleEditPlaybook();
    }
  };

  useEffect(() => {
    if (formValues.status !== "Approved") {
      setFormValues((prevVal) => ({
        ...prevVal,
        versionIncrement: "",
        version: formValues.originalVersion,
        dateApproved: null,
      }));
    }
  }, [formValues.status]);

  useEffect(() => {
    if (playbookDetails?.playbookID) {
      const dateApproved = playbookDetails.dateApproved
        ? dayjs(playbookDetails.dateApproved)
        : null;
      setFormValues({
        playbookName: playbookDetails.playbookName,
        compliance: playbookDetails.compliance,
        preparation: playbookDetails.preparation,
        detection: playbookDetails.detection,
        analysis: playbookDetails.analysis,
        containment: playbookDetails.containment,
        eradication: playbookDetails.eradication,
        recovery: playbookDetails.recovery,
        reporting: playbookDetails.reporting,
        version: playbookDetails.version,
        originalVersion: playbookDetails.version,
        versionIncrement: "",
        dateApproved: dateApproved,
        status: playbookDetails.status,
      });

      setPlaybookFormTitle("Edit Incident Response Playbook");
    } else {
      setFormValues((prevValues) => ({
        ...prevValues,
        status: "Not Approved",
        version: "1.0",
        originalVersion: "1.0",
      }));
      setPlaybookFormTitle("Create Incident Response Playbook");
    }
  }, [playbookDetails]);

  useEffect(() => {
    console.log("isUpdated", isUpdated);
    isUpdatedRef.current = isUpdated;
  }, [isUpdated]);

  const handleExit = async (isEdited: boolean) => {
    console.log("handleExit", isUpdatedRef.current);
    if (playbookDetails.playbookID && !isUpdatedRef.current) {
      const date = new Date();
      const payload = [
        {
          ...playbookDetails,
          isEditedByUser: isEdited,
          isEditedByUserId: userId + "",
          companyProfileID: companyProfileID,
          updateBy: userId,
          updateDate: date,
        },
      ];
      try {
        const resUpdate = await updateIncidentResponsePlaybook(payload);
        console.log("handleExit resUpdate", resUpdate);
        if (resUpdate) {
          setIsUserStillOnPage(false);
          return resUpdate;
        }
      } catch (error) {
        console.log("@@Error", error);
      }
    }
  };

  useEffect(() => {
    return () => {
      handleExit(false);
    };
  }, [location]);

  useEffect(() => {
    console.log("isUserStillOnPage >> ", isUserStillOnPage);
    if (isUserStillOnPage) {
      handleExit(true);
    }
  }, [isUserStillOnPage]);

  useEffect(() => {
    console.log("beforeunload");

    const handleBeforeUnload = async (event: BeforeUnloadEvent) => {
      console.log("handleBeforeUnload", event);
      event.preventDefault();
      await handleExit(false);
      setIsUserStillOnPage(true);
      const message =
        "You have unsaved changes. Are you sure you want to leave? ";
      event.returnValue = message;
      return message;
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  return (
    <Fragment>
      <Grid
        container
        justifyContent="center"
        sx={{
          height: "100%",
          width: "100%",
          padding: "1rem",
          margin: "1rem 0",
          background: "#FFF",
          borderRadius: "2%",
        }}
      >
        <Grid xs={12}>
          <h3 style={{ fontWeight: "400", margin: "0.5rem 0" }}>
            {playbookFormTitle}
          </h3>
        </Grid>

        <Grid container spacing={2} sx={{ height: "100%", width: "90%" }}>
          <Grid xs={12}>
            <h4
              style={{
                color: "lightgrey",
                fontWeight: "400",
                fontStyle: "italic",
                margin: "0.5rem 0",
              }}
            >
              Playbook Name
            </h4>
            <TextField
              fullWidth
              variant="filled"
              size="small"
              id="playbookName"
              value={formValues.playbookName}
              onChange={handleFormChange}
            />
          </Grid>
          <Grid xs={12}>
            <RichTextBoxTabPanel
              readonly={false}
              tabs={tabDetalis}
              handleTabEditerChange={handleTabEditerChange}
            />
          </Grid>
          <AutoVersionIncrement
            disableIncrement={disableVersionChange()}
            version={formValues.version}
            originalVersion={formValues.originalVersion}
            versionIncrement={formValues.versionIncrement}
            onChange={(version, versionIncrement) =>
              setFormValues({
                ...formValues,
                version,
                versionIncrement,
              })
            }
          />
          <Grid xs={6}>
            <h4
              style={{
                color: "lightgrey",
                fontWeight: "400",
                fontStyle: "italic",
                margin: "0.5rem 0",
              }}
            >
              Status
            </h4>
            <FormControl variant="filled" sx={{ minWidth: 200, width: "100%" }}>
              <Select
                id="status"
                value={formValues.status}
                onChange={(e) =>
                  handleFormChange({
                    target: { value: e.target.value, id: "status" },
                  })
                }
              >
                <MenuItem value="Not Approved">Not Approved</MenuItem>
                <MenuItem value="Ready to Approve">Ready to Approve</MenuItem>
                <MenuItem
                  value="Approved"
                  disabled={!incidentResponsePlayBookAccess?.isApproved}
                >
                  Approved
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid xs={6}>
            <h4
              style={{
                color: "lightgrey",
                fontWeight: "400",
                fontStyle: "italic",
                margin: "0.5rem 0",
              }}
            >
              Approval Date
            </h4>
            <DatePicker
              slotProps={{ field: { clearable: true } }}
              value={formValues.dateApproved}
              onChange={(val) =>
                handleFormChange({
                  target: {
                    id: "dateApproved",
                    value: val,
                  },
                })
              }
            />
          </Grid>
          <Grid xs={12} container justifyContent="end">
            <Button
              sx={{ p: 1, m: 1 }}
              variant="outlined"
              size="small"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            {playbookDetails?.playbookID ? (
              <Button
                sx={{ p: 1, m: 1 }}
                variant="contained"
                size="small"
                disabled={
                  disableForm() || !incidentResponsePlayBookAccess?.isUpdate
                }
                onClick={handleEdit}
              >
                Edit
              </Button>
            ) : (
              <Button
                sx={{ p: 1, m: 1 }}
                variant="contained"
                size="small"
                disabled={
                  disableForm() || !incidentResponsePlayBookAccess?.isCreate
                }
                onClick={handleCreatePlaybook}
              >
                CREATE
              </Button>
            )}
          </Grid>
        </Grid>
      </Grid>
      {
        // Edit confirmation
        <DialogBoxComponent
          openDialogBox={dialogState.openEditConfirmationDialog}
          dialogTitle={dialogState.title}
          dialogAction={dialogState.action}
          dialogActionCancel={dialogState.actionCancel}
          dialogBody={dialogState.body}
          handleFunction={handleEditPlaybook}
          handleCancel={handleCloseEditDialog}
        />
      }
      {
        <DialogBoxComponent
          openDialogBox={dialogState.openSuccessDialogBox}
          dialogTitle={dialogState.title}
          dialogAction={dialogState.action}
          dialogBody={dialogState.body}
          handleFunction={handleCloseDialog}
        />
      }
    </Fragment>
  );
};
